import React, { useContext, useEffect, useState } from "react";
import { RotatingLines } from 'react-loader-spinner';
import { EmpdContext } from "../../../../../Context";
import { Document, Page, PDFDownloadLink } from "@react-pdf/renderer";
import { createUseStyles } from "react-jss";
import DashboardCard from "./DashboardCard";
import Results from '../../../../../Components/Results'
import { pick } from 'lodash'
import classNames from "classnames";
import { Slider } from "@base-ui-components/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PatientData from "./PatientData";
import Button from "../../../../../Components/Button";
import DownloadData from "../../../../../Components/DownloadData";
import { globalStyles } from "../../../../../Styles";

interface Props {
  className?: string;
}

interface ResultProps {
	data: any;
	id: string;
}

const useStyles = createUseStyles({
  root: {},
  dataSummaryWrapper: {},
  dataSummary: {
    marginBottom: 50,
  },
  dataSummaryHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1px solid #e1e5ed",
    // "& h4": {
    //   margin: 0,
    // },
  },
  dataSummaryTypeCol: {
    flexBasis: "75%",
  },
  dataSummaryStatusCol: {
    flexBasis: "25%",
    textAlign: "right",
  },
  tableHead: {
    background: "#152E54",
    color: "#fff",
    padding: 10,
    margin: 0,
  },
  statBall: {
    width: 15,
    height: 15,
    borderRadius: "50%",
    marginRight: 10,
  },
  statCol: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",
    alignItems: "center",
  },
  control: {
    marginTop: 75,
    marginBottom: 30,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    width: "100%",
    paddingBlock: "0.75rem",
    touchAction: "none",
    userSelect: "none",
  },
  track: {
    width: "100%",
    height: "1rem",
    background:
      "linear-gradient(90deg, #3660a2, lightblue, yellow, orange, #ff6464)",
    borderRadius: "0.25rem",
    userSelect: "none",
  },
  indicator: {
    borderRadius: "0.25rem",
    userSelect: "none",
  },
  thumb: {
    marginTop: 10,
    width: "0",
    height: "0",
    borderLeft: "15px solid transparent",
    borderRight: "15px solid transparent",
    borderBottom: "35px solid #152E54",
    "& h5": {
      paddingTop: 20,
      width: 100,
      transform: "translateX(-45%)",
    },
  },
  slider: {
    width: "100%",
  },
  others: {
    width: 175,
    height: 60,
    backgroundColor: "#152e541c",
    "& h5": {
      marginTop: -35,
      width: 175,
      textAlign: "center",
    },
  },
  heroButton: {
    alignSelf: "flex-start",
    marginTop: "20px",
    display: "inline-block",
  },
  activeLink: { 
    "&:hover": {
      cursor: "pointer",
    },
  }
});

const ResultDoc = ({data, id}: ResultProps) => {
  return (
    <Document>
	  <Page style={globalStyles.page}>
    	 <Results results={data} id={id} />

	     {/* Footer - (current page / count + disclaimer) */}
    	 {/*
            <Footer />
         */}
      </Page>
    </Document>
  );
};

const makeDownload = (url: string | null, fileName: string) => {
  if (url) {
    return (
      <a href={url} download={fileName}>
        filename
      </a>
    );
  } else {
    return <></>;
  }
};

const DownloadButton: React.FC<{
  data: any;
  id: any;
  classes: any;
  fileName: string;
  handleClose: () => void;
}> = ({ data, classes, fileName, handleClose, id }) => {
  const key =
    Object.keys(data).length > 0 ? `${Object.keys(data).join("-")}` : "none";
  return (
    <PDFDownloadLink
      key={key}
      document={<ResultDoc data={data} id={id}/>}
      fileName={fileName}
    >
      {({ loading, url }) => (
        <Button
          onClick={() => {
            makeDownload(url, fileName);
            handleClose();
          }}
          label={loading ? "Loading document..." : "Download PDF"}
          className={classes.heroButton}
        />
      )}
    </PDFDownloadLink>
  );
};

const useMyContext = () => {
  const context = useContext(EmpdContext);
  if (!context) {
    throw new Error("No provider found for Context");
  }
  return context;
};

const DataSummaryCard = ({ className }: Props) => {
  const context = useMyContext();
  const {
    patientResult,
    selectedCategory,
    setSelectedCategory,
    writePatientData,
    writeComplete,
    mergeData,
    isLoading,
    authUser,
	schemaName,
	demoTable,
  } = context;

  const [data, setData] = useState<any>(null);
  const [openDataDialog, setDataDialogOpen] = useState(false);
  const [openDownloadDialog, setDownloadDialogOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('responseData.pdf')

  useEffect(() => {
    if (authUser && !isLoading && Object.keys(patientResult).length > 0 && patientResult.categories['General Demography'].records.length > 0) {
      let mergedData = mergeData(patientResult);
      setData(mergedData);
      //            findInstanceData(summary.records.columns, setActiveInstance)
      //            setActiveMrn(patientResult.patientQuery.value)
      setFileName(patientResult.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.email`).value.split('@')[0] + '_responses.pdf')
    }
  }, [authUser, isLoading, patientResult]);

  const classes = useStyles();
  const getStatBg = (status: string) => {
    switch (status) {
      case "Incomplete":
      case "Unverified":
        return "#F6BB60";
      case "Complete":
        return "#64cd9b";

      default:
        break;
    }
  };

  const handleCategoryClick = (category: string) => {
        setSelectedCategory({label: category, value: category})
        setDataDialogOpen(true)
  }

  const handleSave = async () => {
    setDataDialogOpen(false)
    await writePatientData();
  };

  const handleSubmit = () => {
    handleSave()
    if (selectedCategory !== null) {
      writeComplete(selectedCategory.label);
    }
  };

  const id = patientResult !== null && Object.keys(patientResult).length > 0 && Object.keys(patientResult.categories).length > 0 && patientResult.categories['General Demography'].records.length > 0 ? patientResult.categories['General Demography'].records[0].columns.find((col: any) => col.fullDatabaseFieldName === `${schemaName}.${demoTable}.first_name`).value + ' ' + patientResult.categories['General Demography'].records[0].columns.find((col: any) => col.fullDatabaseFieldName === `${schemaName}.${demoTable}.last_name`).value : ''

//  const fileName = patientResult.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.email').value.split('@')[0] + '_responses.pdf'

  return (
    <>
      <Dialog
        open={openDataDialog}
        onClose={() => setDataDialogOpen(false)}
        maxWidth={"md"}
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <h3>Add Data</h3>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => {
              setDataDialogOpen(false)
          }}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <PatientData classes={useStyles} />
        </DialogContent>
        <DialogActions sx={{ borderTop: 'solid', borderTopWidth: '1px', justifyContent: 'space-between' }}>
              <Box
                sx={{ flexShrink: 1, alignSelf: { xs: 'flex-start', sm: 'center' } }}
              >
                <Typography >
                  Click <strong>Save</strong> to capture the information you’ve entered so far. You can come back later to continue filling out the surveys.<p/>
                </Typography>
                <Typography >
                  Click <strong>Submit</strong> only when you have completed the entire survey. Once submitted, you will no longer be able to make changes.
                </Typography>
              </Box>
              <Stack
                direction={{
                  xs: 'row-reverse',
                  sm: 'row',
                }}
                sx={{
                  gap: 1,
                  flexShrink: 0,
				  justifyContent: 'flex-end',
                  alignSelf: { xs: 'flex-end', sm: 'center' },
                }}
              >
          <Button className={`disabled`} onClick={() => handleSave()} label="Save" />
          <Button onClick={() => handleSubmit()} label="Submit" />
			  </Stack>
        </DialogActions>
      </Dialog>
      {data && (
          <Dialog
            open={openDownloadDialog}
            onClose={() => setDownloadDialogOpen(false)}
            maxWidth={"md"}
            fullWidth
            scroll="paper"
          >
            <DialogTitle>
              <h3>Select Data to Download</h3>
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => {
                    setDownloadDialogOpen(false)
                }}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
              <DownloadData
                classes={useStyles}
                data={data}
                fileName={fileName}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
              />
            </DialogContent>
            <DialogActions>
              <DownloadButton
                data={pick(data.categories, selectedCategories)}
				id={id}
                classes={classes}
                fileName={fileName}
                handleClose={() => {
                  setDownloadDialogOpen(false);
                }}
              />
            </DialogActions>
          </Dialog>
      )}
      <DashboardCard
        title={"Data Summary"}
        className={classNames(classes.root, className)}
      >
          {isLoading && (
            <RotatingLines
                strokeColor="#152E54"
                strokeWidth="3"
                animationDuration="1.00"
                width="48"
                visible={true}
            />
          )}
          {!isLoading && Object.keys(patientResult).length > 0 && patientResult.categories['General Demography'].records.length > 0 &&(
        	<div className={classes.dataSummaryWrapper}>
          		<div className={classes.dataSummaryHeader}>
            		<h4
              			className={classNames(
		                	classes.dataSummaryTypeCol,
        		        	classes.tableHead
		                )}
            		>
              			TYPE
            		</h4>
            		<h4
		            	className={classNames(
			                classes.dataSummaryStatusCol,
            			    classes.tableHead
              			)}
            		>
             			 STATUS
            		</h4>
          		</div>
              	<div className={classes.dataSummary}>
                	{Object.keys(patientResult.categories).map((category) => {
                  		const status = patientResult.categories[
                    		category
		                ].records[0].columns.find((element: any) => element.fullDatabaseFieldName.match(/.*_complete$/)
						).value;

                  		return (
                    		<div
                      			className={classes.dataSummaryHeader}
                      			key={`${category}-field`}
                    		>
                      			<div className={classes.dataSummaryTypeCol}>
                        			<h4 
										className={classes.activeLink} 
										onClick={() => handleCategoryClick(category)}
									>
										{category}
									</h4>
                      			</div>
                      			<div
                        			className={classNames(
			                        	classes.dataSummaryStatusCol,
            			            	classes.statCol
                        			)}
                      			>
                        			<div
				                        className={classes.statBall}
                				        style={{ background: getStatBg(status) }}
                        			/>
                        			<h4>
                          				{status === `Unverified` || status === `Incomplete`
                            				? `In Review`
                            				: status}
                        			</h4>
                      			</div>
                    		</div>
                  		);
                	})}
            	</div>
            	<Button
                	onClick={() => {
                  		setDownloadDialogOpen(true);
                	}}
                	label={"Download PDF"}
                	className={classes.heroButton}
              	/>
				{/*
              	<h3>Relative Pain</h3>
              	<Slider.Root defaultValue={[50, 25]} className={classes.slider}>
		        	<Slider.Control className={classes.control}>
        		    	<Slider.Track className={classes.track}>
                			<Slider.Indicator className={classes.indicator} />
		                    <Slider.Thumb className={classes.others}>
        		            	<h5>{"OTHERS ARE HERE"}</h5>
                		    </Slider.Thumb>
                    		<Slider.Thumb className={classes.thumb} aria-label="test">
                      			<h5>{"YOU ARE HERE"}</h5>
                    		</Slider.Thumb>
						  </Slider.Track>
                	</Slider.Control>
              	</Slider.Root>
				*/}
			  </div>
           )}
      </DashboardCard>
    </>
  );
};

export default DataSummaryCard;
